import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, createCommentVNode as _createCommentVNode, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "w-full space-y-6" }
const _hoisted_2 = ["onClick"]

import { ref, watch, onMounted } from 'vue'
import { PlusIcon, XIcon } from 'lucide-vue-next'
import AudioUpload from './AudioUpload.vue'
import Button from '../button/Button.vue'
import Label from '../label/Label.vue'
import { toast } from '../toast'
import { Media } from '@/interfaces/Media'

interface AudioItem {
  file: File | null
  title: string
  previewUrl?: string
  mediaId?: number // ID da mídia no servidor
}

interface Props {
  label?: string
  modelValue?: AudioItem[]
  max?: number
  previews?: Media[]
  deleteMediaIds: number[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MultiAudioUpload',
  props: {
    label: { default: 'Áudios do bot' },
    modelValue: { default: () => [] },
    max: { default: 4 },
    previews: {},
    deleteMediaIds: { default: () => [] }
  },
  emits: ['update:modelValue', 'update:deleteMediaIds'],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Lista de áudios
const audioList = ref<AudioItem[]>([])
// Lista de IDs de mídia para exclusão
const mediaIdsToDelete = ref<number[]>([])

// Inicializar com valores do modelo
watch(() => props.modelValue, (newValue) => {
  if (newValue && newValue.length) {
    audioList.value = [...newValue]
  }
}, { immediate: true })

// Inicializar com previews se disponíveis
onMounted(() => {
  if (props.previews && props.previews.length) {
    // Se já temos audioList com itens, vamos atualizar apenas os que têm previewUrl
    if (audioList.value.length === 0) {
      // Se não temos itens, vamos criar a partir das previews
      audioList.value = props.previews.map(preview => ({
        file: null,
        title: preview.fileOriginalName || '',
        previewUrl: preview.source,
        mediaId: preview.id
      }))
    } else {
      // Atualizar os itens existentes com as informações das previews
      props.previews.forEach((preview, index) => {
        if (index < audioList.value.length) {
          audioList.value[index].previewUrl = preview.source
          audioList.value[index].mediaId = preview.id
          if (!audioList.value[index].title) {
            audioList.value[index].title = preview.fileOriginalName || ''
          }
        } else {
          audioList.value.push({
            file: null,
            title: preview.fileOriginalName || '',
            previewUrl: preview.source,
            mediaId: preview.id
          })
        }
      })
    }
    updateModel()
  }
})

// Se não houver áudios iniciais, adicionar um vazio
if (audioList.value.length === 0) {
  addAudio()
}

// Adicionar um novo áudio à lista
function addAudio() {
  if (audioList.value.length >= props.max) {
    toast({
      title: 'Limite de áudios atingido',
      description: 'Se for necessário mais áudios, entre em contato com o suporte',
      variant: 'destructive',
      duration: 3000
    })
    return
  }
  audioList.value.push({
    file: null,
    title: '',
    previewUrl: ''
  })
  console.log(audioList.value)
  updateModel()
}

// Atualizar o arquivo de um áudio
function updateAudio(index: number, file: File | null) {
  if (index >= 0 && index < audioList.value.length) {
    console.log(index, audioList.value[index])
    const audio = audioList.value[index]
    
    // Se havia uma preview e agora estamos substituindo por um novo arquivo
    // if (audio.previewUrl && audio.mediaId && file) {
    //   // Adicionar o ID da mídia à lista de exclusão
    //   addMediaIdToDelete(audio.mediaId)
    //   // Limpar a referência à preview
    //   audio.previewUrl = ''
    //   audio.mediaId = undefined
    // }
    
    audio.file = file
    console.log()
    // Se o arquivo foi adicionado e não há título, usar o nome do arquivo como título
    if (file && !audio.title) {
      audio.title = file.name.split('.')[0]
    }
    
    updateModel()
  }
}

// Atualizar o título de um áudio
function updateAudioTitle(index: number, title: string) {
  if (index >= 0 && index < audioList.value.length) {
    audioList.value[index].title = title
    updateModel()
  }
}

// Remover um áudio da lista
function removeAudio(index: number) {
  if (index >= 0 && index < audioList.value.length) {
    const audio = audioList.value[index]
    
    // Se o áudio removido tinha uma preview, adicionar o ID à lista de exclusão
    if (audio.mediaId) {
      addMediaIdToDelete(audio.mediaId)
    }
    
    audioList.value.splice(index, 1)
    
    // Se a lista ficar vazia, adicionar um novo áudio vazio
    if (audioList.value.length === 0) {
      addAudio()
    }
    
    updateModel()
  }
}

// Adicionar um ID de mídia à lista de exclusão
function addMediaIdToDelete(mediaId: number) {
  if (!mediaIdsToDelete.value.includes(mediaId)) {
    mediaIdsToDelete.value.push(mediaId)
    // Emitir evento para atualizar o v-model de deleteMediaIds
    emit('update:deleteMediaIds', mediaIdsToDelete.value)
  }
}

// Atualizar o modelo
function updateModel() {
  emit('update:modelValue', audioList.value)
}

// Observar mudanças na lista de IDs para exclusão
watch(mediaIdsToDelete, (newValue) => {
  emit('update:deleteMediaIds', newValue)
}, { deep: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_TransitionGroup, {
      name: "audio-grid",
      tag: "div",
      class: "grid grid-cols-1 sm:grid-cols-2 gap-4"
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(audioList.value, (audio, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: "audio-item relative group"
          }, [
            (audioList.value.length > 1)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  onClick: ($event: any) => (removeAudio(index)),
                  class: "absolute -top-2 -right-2 z-10 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-md",
                  title: "Remover áudio"
                }, [
                  _createVNode(_unref(XIcon), { class: "w-4 h-4" })
                ], 8, _hoisted_2))
              : _createCommentVNode("", true),
            _createVNode(AudioUpload, {
              "model-value": audio.file,
              title: audio.title,
              "show-title-field": true,
              "initial-preview-url": audio.previewUrl,
              "onUpdate:modelValue": (file) => updateAudio(index, file),
              "onUpdate:title": (title) => updateAudioTitle(index, title),
              onRemove: ($event: any) => (removeAudio(index))
            }, null, 8, ["model-value", "title", "initial-preview-url", "onUpdate:modelValue", "onUpdate:title", "onRemove"])
          ]))
        }), 128))
      ]),
      _: 1
    }),
    _createVNode(Button, {
      onClick: addAudio,
      class: _normalizeClass([
        'flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white',
        audioList.value.length >= 2 ? 'w-full' : 'w-1/2 px-6'
      ])
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(PlusIcon), { class: "w-5 h-5" }),
        _cache[0] || (_cache[0] = _createTextVNode(" Adicionar mais áudio "))
      ]),
      _: 1
    }, 8, ["class"])
  ]))
}
}

})